'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/shared/services/api';
import type { DashboardLayout } from '../widgets/types';

const STORAGE_KEY = 'sf-dashboard-layout';

// Default-ul v1 (istoric, 26 de widget-uri) — păstrat DOAR pentru migrare:
// hook-ul persistă layout-ul în localStorage la prima vizită, deci „există
// layout salvat" NU înseamnă „utilizatorul a personalizat". Un layout fără `v`
// identic cu v1 = utilizator care n-a atins nimic → primește noul default lean
// (v2). Orice diferență (reordonare / ascundere / adăugare) = personalizare
// reală → se păstrează neatinsă.
const LEGACY_V1_DEFAULT_VISIBLE = [
  'revenue-month', 'invoiced-today', 'collected-today', 'collected-month',
  'outstanding', 'dso', 'cash-position', 'aging', 'anaf-status', 'vat-donut',
  'declaratii', 'spv-status', 'collection-rate', 'yoy-comparison',
  'overdue-trend', 'alerts', 'upcoming-recurring', 'detected-recurring',
  'activity-feed', 'cashflow-forecast', 'whatif', 'revenue-chart',
  'top-clients', 'top-debitors', 'recent-invoices', 'quick-actions',
];

// Widgeturi redenumite/inlocuite: se mapeaza in-place in layouturile
// PERSONALIZATE (migrarea v1→v2 nu le atinge), altfel userii ar pierde
// widgetul fara inlocuitor. 'declaratii' (static, termene partial gresite)
// → 'fiscal-calendar' (server-driven, #V1-5).
const RENAMED_WIDGET_IDS: Record<string, string> = {
  declaratii: 'fiscal-calendar',
};

function renameWidgetIds(l: DashboardLayout): DashboardLayout {
  const map = (id: string) => RENAMED_WIDGET_IDS[id] ?? id;
  return {
    ...l,
    visible: [...new Set(l.visible.map(map))],
    hidden: [...new Set((l.hidden || []).map(map))],
    // latimea personalizata (span/row) urmeaza widgetul redenumit
    ...(l.overrides
      ? { overrides: Object.fromEntries(Object.entries(l.overrides).map(([k, v]) => [map(k), v])) }
      : {}),
  };
}

function isUntouchedV1(parsed: DashboardLayout): boolean {
  if (parsed.v != null) return false;
  if ((parsed.hidden || []).length > 0) return false;
  if (parsed.visible.length !== LEGACY_V1_DEFAULT_VISIBLE.length) return false;
  return parsed.visible.every((id, i) => id === LEGACY_V1_DEFAULT_VISIBLE[i]);
}

export function useDashboardLayout(defaultLayout: DashboardLayout) {
  const [layout, setLayout] = useState<DashboardLayout>(defaultLayout);
  const [hydrated, setHydrated] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DashboardLayout;
        if (parsed && Array.isArray(parsed.visible) && Array.isArray(parsed.hidden)) {
          // Migrare v1→v2: doar cine n-a personalizat primește noul default.
          setLayout(isUntouchedV1(parsed) ? defaultLayout : renameWidgetIds({ ...parsed, v: parsed.v ?? 2 }));
        }
      }
    } catch {}
    setHydrated(true);
    // defaultLayout e o constantă de modul (widget-grid) — stabilă între randări.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on change (after hydration to avoid overwriting)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    } catch {}
  }, [layout, hydrated]);

  // ── Sync cross-device cu serverul (users.dashboard_layout) ──
  // localStorage rămâne cache-ul pentru primul paint; serverul e sursa de
  // adevăr între dispozitive/browsere. Politica: la mount serverul CÂȘTIGĂ
  // (dacă are layout); dacă serverul e gol, urcăm ce avem local (adopție).
  // Salvările ulterioare = last-writer-wins, cu debounce.
  const serverSynced = useRef(false);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    api
      .get<DashboardLayout | null>('/auth/dashboard-layout')
      .then((server) => {
        if (cancelled) return;
        if (server && Array.isArray(server.visible) && Array.isArray(server.hidden)) {
          setLayout(renameWidgetIds({ ...server, v: server.v ?? 2 }));
        } else {
          // Server gol → adoptăm starea locală (inclusiv rezultatul migrării v1→v2).
          api.put('/auth/dashboard-layout', layoutRef.current).catch(() => {});
        }
        serverSynced.current = true;
      })
      .catch(() => {
        // Offline / eroare → funcționăm doar pe localStorage, fără PUT-uri
        // (serverSynced rămâne false ca să nu suprascriem orbește serverul).
      });
    return () => { cancelled = true; };
  }, [hydrated]);

  // Salvare pe server la orice schimbare, debounced. Pornește abia după sync-ul
  // inițial — altfel am putea suprascrie layout-ul de pe server cu default-ul
  // local înainte să-l fi citit.
  useEffect(() => {
    if (!hydrated || !serverSynced.current) return;
    const t = setTimeout(() => {
      api.put('/auth/dashboard-layout', layout).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [layout, hydrated]);

  const setOrder = useCallback((newOrder: string[]) => {
    setLayout((prev) => ({ ...prev, visible: newOrder }));
  }, []);

  const hideWidget = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      visible: prev.visible.filter((v) => v !== id),
      hidden: prev.hidden.includes(id) ? prev.hidden : [...prev.hidden, id],
    }));
  }, []);

  const showWidget = useCallback((id: string) => {
    setLayout((prev) => ({
      ...prev,
      visible: prev.visible.includes(id) ? prev.visible : [...prev.visible, id],
      hidden: prev.hidden.filter((h) => h !== id),
    }));
  }, []);

  // Lățime personalizată per widget (modul „Aranjează", butoanele −/+).
  // span=null → înapoi la implicit (ștergem override-ul, nu stocăm redundant).
  const setWidgetSpan = useCallback((id: string, span: number | null) => {
    setLayout((prev) => {
      const overrides = { ...(prev.overrides ?? {}) };
      if (span == null) {
        delete overrides[id];
        return { ...prev, overrides: Object.keys(overrides).length ? overrides : undefined };
      }
      overrides[id] = { ...(overrides[id] ?? {}), span };
      return { ...prev, overrides };
    });
  }, []);

  const reset = useCallback(() => {
    setLayout(defaultLayout);
  }, [defaultLayout]);

  return {
    layout,
    hydrated,
    setOrder,
    hideWidget,
    showWidget,
    setWidgetSpan,
    reset,
  };
}
